export default {
  slug: "/hair-transplant",
  group: { href: "/hair", label: { en: "Hair restoration", ar: "زراعة الشعر" } },
  heroImage: "/assets/images/ai/services/hair/hairline-approved.webp",

  en: {
    title: "Hair Transplant in Istanbul (FUE)",
    eyebrow: "Hair restoration",
    lead:
      "Hair follicles are moved from the back of the scalp — where they are resistant to hair loss — " +
      "into the areas that have thinned. The result is your own hair, growing where it no longer did.",
    cta: "Book a free assessment",
    heroAlt: "Hair transplant assessment at Platinya Clinic",
    stats: [
      { value: "6–9 h", label: "Procedure time" },
      { value: "1 day", label: "At the clinic" },
      { value: "12–18 months", label: "Final result" },
    ],
    candidate: {
      heading: "Who is a good candidate",
      intro:
        "The limiting factor is not how much hair is missing, but how much donor hair is available " +
        "at the back and sides, and how stable the loss has been.",
      items: [
        "Hair loss that has been stable for roughly a year, not falling week by week",
        "Enough density in the donor area for the number of grafts the bald area needs",
        "Receding hairline, temple loss, or thinning on the crown",
        "Realistic expectations: density improves, but a bald area is never restored to teenage density",
      ],
      note:
        "If the loss is still active, treating it first — medically — protects the result. " +
        "A transplant moves hair; it does not stop the hair you still have from thinning later.",
    },
    techniques: {
      heading: "How the hair is taken and placed",
      intro:
        "The method is chosen by the donor density, the size of the area and the hairstyle you want to keep.",
      items: [
        {
          name: "FUE — follicular unit extraction",
          text:
            "Individual follicles are punched out and re-implanted. No strip of skin is removed, so there " +
            "is no linear scar; the trade-off is that the donor area is shaved and healing takes a few days.",
        },
        {
          name: "DHI — direct hair implantation",
          text:
            "Follicles are placed with a single-use implanter pen, which allows tight control of angle and " +
            "depth in the hairline. Slower, so fewer grafts per session.",
        },
        {
          name: "Hairline design",
          text:
            "The line is drawn with you, sitting and standing, before anything is cut: it has to survive " +
            "ageing and further loss, which is why it is placed conservatively rather than low.",
        },
      ],
    },
    journey: {
      heading: "From consultation to the final result",
      steps: [
        {
          name: "Assessment",
          text:
            "Donor density is measured, the hairline is designed, and blood tests are arranged. " +
            "Photographs are taken from five angles for later comparison.",
        },
        {
          name: "The day itself",
          text:
            "Local anaesthesia with light sedation; you are awake and can eat. The session is long — " +
            "six to nine hours — and is broken by short rests.",
        },
        {
          name: "First ten days",
          text:
            "Small scabs form and are washed off gently from the second day. Swelling across the forehead " +
            "is common and settles within a week.",
        },
        {
          name: "Weeks 3–12",
          text:
            "Most of the transplanted hair sheds — this is expected and does not mean failure. " +
            "New growth starts around month three.",
        },
        {
          name: "Month 12 onward",
          text:
            "Density and thickness continue to improve until 12–18 months. A second session to add " +
            "density, if wanted, is planned no earlier than 12 months.",
        },
      ],
    },
    risks: {
      heading: "What can go wrong",
      intro: "A transplant is a real operation with real limits. These are the ones worth knowing before booking.",
      items: [
        "Swelling, redness and scabbing in the first week, and temporary numbness of the scalp",
        "Follicle survival is never 100%; a proportion of grafts does not grow",
        "Patchy or thin coverage if the planned graft count is not matched by donor supply",
        "Folliculitis — small infected spots in the recipient area, usually treatable",
        "An unnatural hairline if the design is too low or too dense for the donor area",
      ],
      note:
        "Choose the plan by graft count and donor quality, not by the lowest price: a cheap session that " +
        "over-harvests the donor area cannot be undone.",
    },
    faq: {
      heading: "Questions patients ask",
      items: [
        {
          q: "Is it painful?",
          a:
            "The injections sting for a few seconds; after that the scalp is numb and the procedure is " +
            "mostly boring rather than painful. The first night is tender and settles with ordinary pain relief.",
        },
        {
          q: "Will there be a scar?",
          a:
            "FUE leaves tiny circular marks in the donor area that fade to near-invisible at normal hair " +
            "length. Strip surgery leaves a fine line across the back of the scalp.",
        },
        {
          q: "When can I go back to work?",
          a:
            "Desk work after two to three days, once the swelling has settled. Heavy exercise, saunas and " +
            "swimming wait about two weeks.",
        },
        {
          q: "Does the new hair last?",
          a:
            "Transplanted follicles keep the behaviour of the donor area and are generally permanent. " +
            "The hair around them can still thin, which is why medical treatment is often continued.",
        },
      ],
    },
    ctaBand: {
      heading: "Send your photographs for a first opinion",
      text:
        "Photos of the hairline, the crown and the donor area are enough to say whether a transplant is " +
        "sensible, roughly how many grafts, and what it would cost.",
      button: "Send your photos",
    },
    disclaimer:
      "General information, not medical advice. Suitability can only be judged after examining the scalp and " +
      "the medical history; results vary between patients.",
  },

  ar: {
    title: "زراعة الشعر في إسطنبول (FUE)",
    eyebrow: "زراعة الشعر",
    lead:
      "تُنقل بصيلات الشعر من مؤخرة الرأس — وهي المنطقة التي تقاوم التساقط — إلى المناطق التي خفّ فيها الشعر. " +
      "النتيجة شعرك أنت، ينمو في مكان لم يعد ينمو فيه.",
    cta: "احجز تقييماً مجانياً",
    heroAlt: "تقييم زراعة الشعر في عيادة بلاتينيا",
    stats: [
      { value: "٦–٩ ساعات", label: "مدة الجلسة" },
      { value: "يوم واحد", label: "في العيادة" },
      { value: "١٢–١٨ شهراً", label: "النتيجة النهائية" },
    ],
    candidate: {
      heading: "من هو المرشّح المناسب",
      intro:
        "العامل الحاسم ليس مقدار الشعر المفقود، بل كمية الشعر المانح المتوفّرة في المؤخرة والجانبين، " +
        "ومدى استقرار التساقط.",
      items: [
        "تساقط مستقر منذ نحو سنة، لا يتساقط أسبوعاً بعد أسبوع",
        "كثافة كافية في المنطقة المانحة لعدد البصيلات التي تحتاجها المنطقة الفارغة",
        "تراجع خط الشعر أو الصدغين أو خفّة في منطقة التاج",
        "توقّعات واقعية: الكثافة تتحسّن، لكن المنطقة الفارغة لا تعود بكثافة المراهقة",
      ],
      note:
        "إذا كان التساقط ما زال نشطاً، فمعالجته أولاً — دوائياً — تحمي النتيجة. زراعة الشعر تنقل الشعر، " +
        "لكنها لا تمنع الشعر المتبقّي من الخفّة لاحقاً.",
    },
    techniques: {
      heading: "كيف يُؤخذ الشعر ويُزرع",
      intro: "تُختار الطريقة حسب كثافة المنطقة المانحة وحجم المنطقة وتسريحة الشعر التي تريد الحفاظ عليها.",
      items: [
        {
          name: "FUE — الاقتطاف Follicular Unit Extraction",
          text:
            "تُقتطف البصيلات واحدة واحدة وتُزرع من جديد. لا يُقطع شريط من الجلد فلا يبقى أثر خطّي؛ " +
            "المقابل أن المنطقة المانحة تُحلق ويستغرق شفاؤها أياماً قليلة.",
        },
        {
          name: "DHI — الزرع المباشر",
          text:
            "تُوضع البصيلات بقلم زرع يُستخدم مرة واحدة، ما يمنح تحكّماً دقيقاً في زاوية وعمق خط الشعر. " +
            "أبطأ، لذلك يكون عدد البصيلات في الجلسة أقل.",
        },
        {
          name: "تصميم خط الشعر",
          text:
            "يُرسم الخط معك وأنت جالس وواقف قبل أي قصّ: يجب أن يصمد أمام التقدّم في العمر والتساقط اللاحق، " +
            "ولهذا يوضع متحفّظاً لا منخفضاً.",
        },
      ],
    },
    journey: {
      heading: "من الاستشارة إلى النتيجة النهائية",
      steps: [
        {
          name: "التقييم",
          text:
            "تُقاس كثافة المنطقة المانحة ويُرسم خط الشعر وتُطلب التحاليل. وتُلتقط صور من خمس زوايا " +
            "للمقارنة لاحقاً.",
        },
        {
          name: "يوم العملية",
          text:
            "تخدير موضعي مع تهدئة خفيفة؛ تبقى واعياً ويمكنك الأكل. الجلسة طويلة — ست إلى تسع ساعات — " +
            "وتُقطع بفترات راحة قصيرة.",
        },
        {
          name: "أول عشرة أيام",
          text:
            "تتكوّن قشور صغيرة تُغسل بلطف من اليوم الثاني. التورّم في الجبهة شائع ويهدأ خلال أسبوع.",
        },
        {
          name: "الأسبوع الثالث إلى الثاني عشر",
          text:
            "معظم الشعر المزروع يتساقط — وهذا متوقّع ولا يعني الفشل. يبدأ النمو الجديد في الشهر الثالث تقريباً.",
        },
        {
          name: "من الشهر الثاني عشر",
          text:
            "تتحسّن الكثافة والسماكة حتى ١٢–١٨ شهراً. وجلسة ثانية لإضافة كثافة، إن رغبت، تُخطّط بعد سنة على الأقل.",
        },
      ],
    },
    risks: {
      heading: "ما قد يسوء",
      intro: "الزراعة عملية حقيقية لها حدود حقيقية. هذه أهمّها قبل الحجز.",
      items: [
        "تورّم واحمرار وقشور في الأسبوع الأول، وتنميل مؤقّت في فروة الرأس",
        "بقاء البصيلات ليس ١٠٠٪؛ نسبة من البصيلات لا تنمو",
        "تغطية غير متساوية أو خفيفة إذا لم يوازن عدد البصيلات المخطّط مخزون المنطقة المانحة",
        "التهاب الجريبات — نقاط صغيرة ملتهبة في المنطقة المزروعة، غالباً قابلة للعلاج",
        "خط شعر غير طبيعي إذا كان التصميم منخفضاً أو كثيفاً أكثر من طاقة المنطقة المانحة",
      ],
      note:
        "اختر الخطة بعدد البصيلات وجودة المنطقة المانحة، لا بأرخص سعر: جلسة رخيصة تُستهلك فيها المنطقة " +
        "المانحة بإفراط لا يمكن إصلاحها.",
    },
    faq: {
      heading: "أسئلة يطرحها المرضى",
      items: [
        {
          q: "هل هي مؤلمة؟",
          a:
            "الإبر تؤلم ثوانٍ قليلة، بعدها تصبح فروة الرأس مخدّرة وتكون الجلسة مملّة أكثر منها مؤلمة. " +
            "الليلة الأولى مؤلمة قليلاً وتهدأ بمسكّن عادي.",
        },
        {
          q: "هل يبقى أثر؟",
          a:
            "FUE تترك علامات دائرية دقيقة في المنطقة المانحة تبهت حتى تكاد تختفي بطول الشعر العادي. " +
            "أما طريقة الشريط فتترك خطاً رفيعاً في مؤخرة الرأس.",
        },
        {
          q: "متى أعود للعمل؟",
          a:
            "العمل المكتبي بعد يومين إلى ثلاثة أيام، بعد هدوء التورّم. الرياضة العنيفة والساونا والسباحة " +
            "تنتظر نحو أسبوعين.",
        },
        {
          q: "هل يدوم الشعر الجديد؟",
          a:
            "البصيلات المزروعة تحتفظ بسلوك المنطقة المانحة وهي دائمة عموماً. الشعر حولها قد يخفّ، " +
            "ولهذا يُستمر غالباً بالعلاج الدوائي.",
        },
      ],
    },
    ctaBand: {
      heading: "أرسل صورك لرأي أوّلي",
      text:
        "صور لخط الشعر ومنطقة التاج والمنطقة المانحة تكفي لنقول هل الزراعة مناسبة، وكم بصيلة تقريباً، " +
        "وما التكلفة.",
      button: "أرسل صورك",
    },
    disclaimer:
      "معلومات عامة وليست نصيحة طبية. لا يمكن الحكم على الملاءمة إلا بعد فحص فروة الرأس والتاريخ المرضي؛ " +
      "والنتائج تختلف من مريض لآخر.",
  },
};
